import { motion } from 'framer-motion';
import { ChevronRight, ArrowRight, Pencil, Trash2, Star, Plus, Calendar, Clock } from 'lucide-react';

function Stepper({ active }) {
  const steps = ['Basics', 'Build', 'Configure', 'Review'];
  return (
    <div className="flex items-center justify-center gap-0 py-5 px-6 bg-[#FAFAFA] rounded-2xl mb-4">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center">
          <div className="flex flex-col items-center gap-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${
              i < active ? 'bg-teal-600 border-teal-600 text-white' :
              i === active ? 'bg-white border-teal-600 text-teal-600' :
              'bg-white border-[#E5E7EB] text-[#737373]'
            }`}>{i < active ? '✓' : <span className="w-1.5 h-1.5 rounded-full bg-current" />}</div>
            <span className={`text-[12px] font-body ${i <= active ? 'font-medium text-[#0A0A0A]' : 'text-[#737373]'}`}>{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`w-24 h-[2px] mx-2 mt-[-16px] ${i < active ? 'bg-teal-600' : 'bg-[#E5E7EB]'}`} />}
        </div>
      ))}
    </div>
  );
}

const questions = [
  { q: 'What is your favourite fruit?', type: 'radio', opts: ['Apple', 'Banana', 'Cantaloupe', 'Dried Fruits'] },
  { q: 'Where have you traveled to in the last 6 months?', type: 'checkbox', opts: ['London', 'Paris', 'Tokyo', 'Munich'], logic: true },
  { q: 'Please rate the quality of service provided', type: 'rating' },
  { q: 'What are your favourite things to do after work?', type: 'text', placeholder: 'Swimming, cooking, etc' },
  { q: 'When would you like to attend?', type: 'datetime' },
];

export default function SurveyBuildScene({ onNext }) {
  return (
    <div data-testid="scene-survey-build" className="flex flex-col items-center justify-start h-full px-8 pt-3 overflow-y-auto">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full max-w-3xl">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="text-[12px] font-body text-[#737373] mb-2">
          Home <ChevronRight size={12} className="inline" /> Survey <ChevronRight size={12} className="inline" /> <span className="font-medium text-[#0A0A0A]">Create new survey</span>
        </motion.div>
        <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mb-3">Create New Survey</h2>

        <Stepper active={1} />

        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-body text-[#0A0A0A]">&lt; Back</span>
          <div className="flex gap-2 text-[12px] font-body">
            <span className="text-teal-600 font-medium">Preview survey</span>
            <span className="px-3 py-1 rounded border border-[#E5E7EB] text-[#0A0A0A]">Save</span>
          </div>
        </div>

        {/* Questions */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="bg-[#FAFAFA] rounded-2xl border border-[#E5E7EB] p-5 mb-3">
          <h3 className="font-heading font-bold text-base text-[#0A0A0A] mb-1">Build questionnaire</h3>
          <p className="font-body text-xs text-[#737373] mb-4">Add and organise questions for your survey</p>

          <div className="space-y-3">
            {questions.map((q, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.2 }}
                className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                {q.logic && <span className="text-[9px] font-body font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 mb-2 inline-block">Logic-based</span>}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-body font-bold text-[13px] text-[#0A0A0A]">Q{i + 1}  {q.q}<span className="text-red-500">*</span></p>
                    {q.type === 'radio' && <div className="mt-2 ml-4 space-y-1">{q.opts.map(o => <div key={o} className="flex items-center gap-2 text-[12px] font-body text-[#737373]"><div className="w-3 h-3 rounded-full border border-[#737373]" />{o}</div>)}</div>}
                    {q.type === 'checkbox' && <div className="mt-2 ml-4 space-y-1">{q.opts.map(o => <div key={o} className="flex items-center gap-2 text-[12px] font-body text-[#737373]"><div className="w-3 h-3 rounded border border-[#737373]" />{o}</div>)}</div>}
                    {q.type === 'rating' && <div className="mt-2 flex items-center gap-1"><span className="text-[11px] font-body text-[#737373]">Poor</span>{[1,2,3,4,5].map(s => <Star key={s} size={14} className="text-[#E5E7EB]" />)}<span className="text-[11px] font-body text-[#737373]">Excellent</span></div>}
                    {q.type === 'text' && <div className="mt-2 px-3 py-2 rounded-lg border border-[#E5E7EB] text-[12px] font-body text-[#737373]">{q.placeholder}</div>}
                    {q.type === 'datetime' && <div className="mt-2 grid grid-cols-2 gap-3"><div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E7EB] text-[12px] font-body text-[#737373]"><Calendar size={12} />DD/MM/YYYY</div><div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E7EB] text-[12px] font-body text-[#737373]"><Clock size={12} />HH:MM AM</div></div>}
                  </div>
                  <div className="flex gap-2 ml-3 flex-shrink-0">
                    <Pencil size={14} className="text-[#737373]" />
                    <Trash2 size={14} className="text-[#737373]" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
            className="flex justify-center mt-4">
            <button className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#E5E7EB] text-sm font-body text-[#0A0A0A]">
              <Plus size={14} /> Add question
            </button>
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }} className="flex justify-center mb-4">
          <button onClick={onNext} className="flex items-center gap-2 px-8 py-2.5 rounded-xl bg-teal-600 text-white font-body font-bold text-sm cursor-pointer">
            Next <ArrowRight size={16} />
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}
